/** Global KPI filter bar. A focused v1 subset of the spec's 10 dimensions
 *  (date range + internal/external + country); the backend KpiFilter accepts
 *  the rest and more selects can be added here without touching the API. */
import { useTranslation } from "react-i18next";

import { CountrySelector } from "@/components/CountrySelector";

import type { KpiFilterState } from "./types";

interface Props {
  value: KpiFilterState;
  onChange: (next: KpiFilterState) => void;
}

export function FilterBar({ value, onChange }: Props) {
  const { t } = useTranslation();
  const set = (patch: Partial<KpiFilterState>) => onChange({ ...value, ...patch });

  return (
    <div className="card flex flex-wrap items-end gap-4">
      <label className="flex flex-col gap-1">
        <span className="label">{t("analytics.dateFrom")}</span>
        <input
          type="date"
          className="input"
          value={value.date_from ?? ""}
          onChange={(e) => set({ date_from: e.target.value })}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="label">{t("analytics.dateTo")}</span>
        <input
          type="date"
          className="input"
          value={value.date_to ?? ""}
          onChange={(e) => set({ date_to: e.target.value })}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="label">{t("analytics.operatorType")}</span>
        <select
          className="input"
          value={value.operator_type ?? ""}
          onChange={(e) =>
            set({ operator_type: e.target.value as KpiFilterState["operator_type"] })
          }
        >
          <option value="">{t("analytics.all")}</option>
          <option value="internal">{t("analytics.internal")}</option>
          <option value="external">{t("analytics.external")}</option>
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className="label">{t("analytics.country")}</span>
        <CountrySelector
          value={value.country ?? ""}
          onChange={(code) => set({ country: code })}
          allowEmpty
          className="min-w-[11rem]"
          aria-label={t("analytics.country")}
        />
      </label>
      {(value.date_from || value.date_to || value.operator_type || value.country) && (
        <button type="button" className="btn btn-ghost" onClick={() => onChange({})}>
          {t("analytics.clear")}
        </button>
      )}
    </div>
  );
}
